"use client";

import React from "react";
import { Facebook, Twitter, Linkedin, Instagram, ArrowRight, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider } from "@/integrations/forms/FormProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const [email, setEmail] = React.useState("");
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmail("");
  };
  return <form className="flex gap-2" onSubmit={handleSubmit}>
      <Input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} className="bg-input/30 border-border/50 focus-visible:ring-primary/50 text-sm" required />
      <Button type="submit" size="icon" variant="default" className="shrink-0 group">
        <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
        <span className="sr-only">Subscribe</span>
      </Button>
    </form>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const legalLinks = [{
    label: "Terms of Service",
    href: "#"
  }, {
    label: "Privacy Policy",
    href: "#"
  }, {
    label: "Risk Warning",
    href: "#"
  }, {
    label: "Cookie Policy",
    href: "#"
  }];
  const socialLinks = [{
    icon: Twitter,
    href: "#",
    label: "Twitter"
  }, {
    icon: Linkedin,
    href: "#",
    label: "LinkedIn"
  }, {
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }];
  return <section id="footer" className="dark bg-background text-foreground pt-16 pb-6 border-t border-border/50 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {}
          <div className="flex flex-col gap-6">
            <Link to="/">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604002/img/tradesphere_logo_dark.png" alt="TradeSphere" className="h-7 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Empowering global investors with institutional-grade technology, lightning-fast execution, and uncompromising security.
            </p>
          </div>

          {}
          <div>
            <h3 className="font-semibold text-foreground mb-6 tracking-tight">Platform</h3>
            <MenuProvider menu_id="42">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="font-semibold text-foreground mb-6 tracking-tight">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => <li key={link.label} data-index={i}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div>
            <h3 className="font-semibold text-foreground mb-6 tracking-tight">Stay Updated</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe to our newsletter for the latest market insights and platform updates.
            </p>
            <FormProvider>
              <NewsletterForm />
            </FormProvider>
            
            <div className="flex items-center gap-4 mt-8">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.label} to={social.href} data-index={i} className="text-muted-foreground hover:text-primary transition-colors duration-200 p-2 -m-2" aria-label={social.label}>
                    <Icon className="size-5" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        <Separator className="bg-border/40 mb-6" />

        {}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 text-xs text-muted-foreground">
          <p className="shrink-0">
            &copy; {currentYear} TradeSphere. All rights reserved.
          </p>
          
          <div className="flex items-start gap-3 max-w-3xl bg-muted/20 p-3 rounded-md border border-border/30">
            <ShieldAlert className="size-4 shrink-0 text-destructive/80 mt-0.5" />
            <p className="leading-relaxed opacity-80">
              <strong className="text-foreground font-medium">High Risk Investment Warning:</strong> Trading Foreign Exchange (Forex) and Contracts for Differences (CFDs) is highly speculative, carries a high level of risk and may not be suitable for all investors. You may sustain a loss of some or all of your invested capital, therefore, you should not speculate with capital that you cannot afford to lose.
            </p>
          </div>
        </div>
      </div>
    </section>;
}